import { useState, useEffect, useRef, useCallback } from 'react'
import { motion } from 'framer-motion'
import ReactMarkdown from 'react-markdown'
import { useSynopsis, useSynopsisOperations } from '../hooks/useFirestore'
import { useAuth } from '../context/AuthContext'
import { useProject } from '../context/ProjectContext'
import { BRANDING } from '../config/branding'
import { Icon } from '../components/common'
import './SynopsisView.css'

// Page transition variants
const pageVariants = {
  initial: { 
    opacity: 0, 
    y: 20,
    filter: 'blur(4px)'
  },
  animate: { 
    opacity: 1, 
    y: 0,
    filter: 'blur(0px)',
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1]
    }
  },
  exit: { 
    opacity: 0, 
    y: -20,
    filter: 'blur(4px)',
    transition: {
      duration: 0.2
    }
  }
}

// Default synopsis content - uses branding config for title
const getDefaultSynopsis = () => `# ${BRANDING.defaults.synopsisTitle}

Welcome to the ${BRANDING.name} story dashboard. Start writing your synopsis here, or import an existing markdown file.

## Layers

- **Layer A** — Present Day (Primary Timeline)
- **Layer B** — Flashbacks (Secondary Timeline)
- **Layer C** — Parallel Story
- **Layer D** — Framing Device

---

Start editing to add your story content...
`

// Custom markdown components with layer highlighting
const MarkdownComponents = {
  // Process text to highlight layer mentions
  p: ({ children, ...props }) => {
    return <p {...props}>{processLayerText(children)}</p>
  },
  li: ({ children, ...props }) => {
    return <li {...props}>{processLayerText(children)}</li>
  },
  strong: ({ children, ...props }) => {
    // Check if this is a layer mention
    const text = getTextContent(children)
    const layerMatch = text.match(/Layer\s+([ABCD])/i)
    if (layerMatch) {
      const layer = layerMatch[1].toLowerCase()
      return (
        <strong {...props} className={`layer-highlight layer-${layer}`}>
          {children}
        </strong>
      )
    }
    return <strong {...props}>{children}</strong>
  },
}

// Helper to get text content from React children
function getTextContent(children) {
  if (typeof children === 'string') return children
  if (Array.isArray(children)) {
    return children.map(getTextContent).join('')
  }
  if (children?.props?.children) {
    return getTextContent(children.props.children)
  }
  return ''
}

// Process text to add layer highlighting
function processLayerText(children) {
  if (typeof children === 'string') {
    // Match Layer A, Layer B, etc.
    const parts = children.split(/(Layer\s+[ABCD])/gi)
    if (parts.length === 1) return children
    
    return parts.map((part, i) => {
      const match = part.match(/Layer\s+([ABCD])/i)
      if (match) {
        const layer = match[1].toLowerCase()
        return (
          <span key={i} className={`layer-tag layer-tag--${layer}`}>
            {part}
          </span>
        )
      }
      return part
    })
  }
  return children
}

/**
 * SynopsisView - Markdown editor with layer color-coding
 */
export default function SynopsisView() {
  // Auth context for user info
  const { user } = useAuth()
  
  // Project context for scoped data access and permissions
  const { projectId, can } = useProject()
  const canEdit = can('edit')
  
  // View mode: 'edit', 'preview', or 'split'
  // Default to 'preview' if user can't edit
  const [mode, setMode] = useState(canEdit ? 'split' : 'preview')
  
  // Local content state (for editing)
  const [content, setContent] = useState('')
  
  // Save status: 'idle', 'saving', 'saved', 'error'
  const [saveStatus, setSaveStatus] = useState('idle')
  
  // File input ref for import
  const fileInputRef = useRef(null)
  
  // Debounce timer ref
  const saveTimerRef = useRef(null)
  
  // Firestore data and operations (project-scoped)
  const { data: synopsisDoc, loading, error } = useSynopsis(projectId)
  const { save } = useSynopsisOperations(projectId)
  
  // Initialize content from Firestore
  useEffect(() => {
    if (synopsisDoc?.content !== undefined) {
      setContent(synopsisDoc.content)
    } else if (!loading && !synopsisDoc) {
      // No document exists yet, use default
      setContent(getDefaultSynopsis())
    }
  }, [synopsisDoc, loading])
  
  // Auto-save with debounce (only if user has edit permission)
  const handleContentChange = useCallback((newContent) => {
    if (!canEdit) return // Don't allow changes if no edit permission
    
    setContent(newContent)
    setSaveStatus('saving')
    
    // Clear existing timer
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current)
    }
    
    // Set new timer for auto-save (1 second debounce)
    saveTimerRef.current = setTimeout(async () => {
      try {
        await save({
          content: newContent,
          updatedBy: user?.uid || 'anonymous',
        })
        setSaveStatus('saved')
        
        // Reset to idle after 2 seconds
        setTimeout(() => setSaveStatus('idle'), 2000)
      } catch (err) {
        console.error('Failed to save synopsis:', err)
        setSaveStatus('error')
      }
    }, 1000)
  }, [save, user, canEdit])
  
  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current)
      }
    }
  }, [])
  
  // Handle file import
  const handleImport = async (event) => {
    const file = event.target.files?.[0]
    if (!file) return
    
    try {
      const text = await file.text()
      handleContentChange(text)
    } catch (err) {
      console.error('Failed to read file:', err)
      setSaveStatus('error')
    }
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }
  
  // Start editing with default content
  const handleStartEditing = () => {
    setMode('edit')
    if (!content) {
      setContent(getDefaultSynopsis())
    }
  }
  
  // Get save status text and class
  const getSaveStatusInfo = () => {
    switch (saveStatus) {
      case 'saving':
        return { text: 'Saving...', className: 'saving' }
      case 'saved':
        return { text: 'Saved', className: 'saved' }
      case 'error':
        return { text: 'Error saving', className: 'error' }
      default:
        return { text: 'Auto-save enabled', className: '' }
    }
  }
  
  const statusInfo = getSaveStatusInfo()
  
  // Loading state
  if (loading) {
    return (
      <motion.div 
        className="synopsis-view"
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
      >
        <div className="synopsis-view__loading">
          <div className="synopsis-view__spinner" />
          <p>Loading synopsis...</p>
        </div>
      </motion.div>
    )
  }
  
  // Error state
  if (error) {
    return (
      <motion.div 
        className="synopsis-view"
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
      >
        <header className="synopsis-view__header">
          <div className="synopsis-view__title-group">
            <span className="synopsis-view__icon"><Icon name="synopsis" size={28} /></span>
            <h1 className="synopsis-view__title">Synopsis</h1>
          </div>
        </header>
        <div className="synopsis-view__empty">
          <div className="synopsis-view__empty-icon"><Icon name="warning" size={48} /></div>
          <h2>Error Loading Synopsis</h2>
          <p>{error.message}</p>
        </div>
      </motion.div>
    )
  }
  
  // Empty state (no content yet)
  if (!content && !loading) {
    return (
      <motion.div 
        className="synopsis-view"
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
      >
        <header className="synopsis-view__header">
          <div className="synopsis-view__title-group">
            <span className="synopsis-view__icon"><Icon name="synopsis" size={28} /></span>
            <h1 className="synopsis-view__title">Synopsis</h1>
          </div>
        </header>
        
        <div className="synopsis-view__empty">
          <div className="synopsis-view__empty-icon"><Icon name="synopsis" size={48} /></div>
          <h2>No Synopsis Yet</h2>
          <p>Start writing your story synopsis or import an existing markdown file.</p>
          <div className="synopsis-view__empty-actions">
            <button 
              className="synopsis-view__empty-btn synopsis-view__empty-btn--primary"
              onClick={handleStartEditing}
            >
              <Icon name="edit" size={16} /> Start Writing
            </button>
            <button 
              className="synopsis-view__empty-btn synopsis-view__empty-btn--secondary"
              onClick={() => fileInputRef.current?.click()}
            >
                <span><Icon name="import" size={16} /></span> Import File
            </button>
          </div>
        </div>
        
        <input
          ref={fileInputRef}
          type="file"
          accept=".md,.markdown,.txt"
          onChange={handleImport}
          className="synopsis-view__file-input"
        />
      </motion.div>
    )
  }

  return (
    <motion.div 
      className="synopsis-view"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      {/* Header */}
      <header className="synopsis-view__header">
        <div>
          <div className="synopsis-view__title-group">
            <span className="synopsis-view__icon"><Icon name="synopsis" size={28} /></span>
            <h1 className="synopsis-view__title">Synopsis</h1>
          </div>
          <p className="synopsis-view__subtitle">Story overview with layer color-coding</p>
        </div>
        
        <div className="synopsis-view__controls">
          {/* Mode Toggle */}
          <div className="synopsis-view__mode-toggle">
            {canEdit && (
              <button
                className={`synopsis-view__mode-btn ${mode === 'edit' ? 'synopsis-view__mode-btn--active' : ''}`}
                onClick={() => setMode('edit')}
              >
                <Icon name="edit" size={16} />
                <span>Edit</span>
              </button>
            )}
            {canEdit && (
              <button
                className={`synopsis-view__mode-btn ${mode === 'split' ? 'synopsis-view__mode-btn--active' : ''}`}
                onClick={() => setMode('split')}
              >
                <span>◫</span>
                <span>Split</span>
              </button>
            )}
            <button
              className={`synopsis-view__mode-btn ${mode === 'preview' ? 'synopsis-view__mode-btn--active' : ''}`}
              onClick={() => setMode('preview')}
            >
              <Icon name="eye" size={16} />
              <span>Preview</span>
            </button>
          </div>
          
          {/* Save Status - only shown if user can edit */}
          {canEdit && (
            <div className={`synopsis-view__save-status synopsis-view__save-status--${statusInfo.className}`}>
              <span className="synopsis-view__save-indicator" />
              <span>{statusInfo.text}</span>
            </div>
          )}
          
          {/* Read-only indicator */}
          {!canEdit && (
            <div className="synopsis-view__readonly-badge">
                <span><Icon name="eye" size={16} /></span>
              <span>View only</span>
            </div>
          )}
          
          {/* Import Button - only shown if user can edit */}
          {canEdit && (
            <button 
              className="synopsis-view__import-btn"
              onClick={() => fileInputRef.current?.click()}
            >
                <span><Icon name="import" size={16} /></span>
              <span>Import</span>
            </button>
          )}
          
          <input
            ref={fileInputRef}
            type="file"
            accept=".md,.markdown,.txt"
            onChange={handleImport}
            className="synopsis-view__file-input"
          />
        </div>
      </header>
      
      {/* Layer Legend */}
      <div className="synopsis-view__legend">
        <div className="synopsis-view__legend-item">
          <span className="synopsis-view__legend-dot synopsis-view__legend-dot--a" />
          <span>Layer A — 2026 iPhone</span>
        </div>
        <div className="synopsis-view__legend-item">
          <span className="synopsis-view__legend-dot synopsis-view__legend-dot--b" />
          <span>Layer B — 1994 VHS</span>
        </div>
        <div className="synopsis-view__legend-item">
          <span className="synopsis-view__legend-dot synopsis-view__legend-dot--c" />
          <span>Layer C — Corporate</span>
        </div>
        <div className="synopsis-view__legend-item">
          <span className="synopsis-view__legend-dot synopsis-view__legend-dot--d" />
          <span>Layer D — Time Travel</span>
        </div>
      </div>
      
      {/* Content */}
      <div className={`synopsis-view__content synopsis-view__content--${mode}`}>
        {/* Editor */}
        <div className="synopsis-view__editor">
          <textarea
            className="synopsis-view__textarea"
            value={content}
            onChange={(e) => handleContentChange(e.target.value)}
            placeholder="Start writing your synopsis..."
            spellCheck={false}
          />
        </div>
        
        {/* Preview */}
        <div className="synopsis-view__preview">
          <div className="synopsis-view__markdown">
            <ReactMarkdown components={MarkdownComponents}>
              {content}
            </ReactMarkdown>
          </div>
        </div>
      </div>
    </motion.div>
  )
}

